import { Card, CardContent } from '@/components/ui/Card';
import { useT } from '@/lib/i18n/useT';
import type { XkStationSummary } from '@/lib/api/types';
import { fmtInt } from '@/features/insights/engine';
import { HeroTiles } from '@/features/insights/charts/HeroTiles';
import { StationTimeline } from './charts/StationTimeline';
import { StationMerch } from './charts/StationMerch';
import { StationTopAnswers } from './charts/StationTopAnswers';

interface DashboardTabProps {
  summary: XkStationSummary;
}

/**
 * El dashboard de la estación con la misma gramática que el informe del
 * evento y que Insights: la tira oscura de cifras, la línea de tiempo en
 * panel nocturno, el anillo del merch con su fila por artículo y las
 * respuestas más frecuentes. Todo se llena al entrar en pantalla.
 */
export function DashboardTab({ summary }: DashboardTabProps) {
  const t = useT();
  const hasResponses = summary.responses.totalResponses > 0;
  const hasInventory = summary.inventoryTotals.quantity > 0;

  // Sin interactions ni inventario no hay nada que dibujar: una sola tarjeta
  // vacía en vez de cuatro iguales.
  if (!hasResponses && !hasInventory) {
    return (
      <Card>
        <CardContent>
          <p className="py-12 text-center text-sm text-[var(--color-fg-muted)]">
            {t('stations.detail.dashboardEmpty')}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { quantity, used, available } = summary.inventoryTotals;
  const pct = quantity ? Math.round((used / quantity) * 100) : 0;
  const r = summary.responses;

  return (
    <div className="space-y-5" data-report-view>
      <HeroTiles
        title={t('stations.dash.heroTitle')}
        chip={quantity ? t('stations.dash.usedChip', { pct: String(pct) }) : undefined}
        tiles={[
          { label: t('stations.dash.interactions'), value: fmtInt(r.totalResponses), number: r.totalResponses, format: fmtInt, hint: t('stations.dash.interactionsHint'), tone: 'accent' },
          { label: t('stations.dash.unitsOut'), value: fmtInt(used), number: used, format: fmtInt, hint: t('stations.dash.ofAllocated', { n: fmtInt(quantity) }) },
          { label: t('dash.seg.inPlay'), value: fmtInt(available), number: available, format: fmtInt, hint: t('stations.dash.remainingHint') },
          { label: t('stations.dash.last24h'), value: fmtInt(r.last24hCount), number: r.last24hCount, format: fmtInt, hint: t('stations.dash.last7dHint', { n: fmtInt(r.last7dCount) }) },
        ]}
      />
      <StationTimeline data={r} />
      <StationMerch inventory={summary.inventory} totals={summary.inventoryTotals} />
      <StationTopAnswers components={r.topAnswers} />
    </div>
  );
}
